/* W11-FM — Décision manuelle Financial Markets : capter ce qui se décide hors moteur,
 * avec la mémoire de l'organisation consultée AVANT de décider (P-2). */
import { useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { CandlestickChart, Plus } from "lucide-react";
import { MemoryRecallPanel } from "@/components/MemoryRecallPanel";

export function ManualFmDecisionForm({ onDone }) {
  const [f, setF] = useState({
    instrument: "", title: "", thesis: "", capital: "",
    pWin: 0.6, upside: "", downside: "", source: "", confidence: 0.6, horizon: 14,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const p = Number(f.pWin);
  const submit = async () => {
    if (!f.instrument.trim() || !f.title.trim()) return notify.error("Instrument et titre requis.");
    if (!f.source.trim()) return notify.error("Source de l'estimation requise — NOAM n'accepte aucune probabilité non sourcée.");
    const up = Number(f.upside), down = Number(f.downside), cap = Number(f.capital);
    if (!(up > 0) || !(down > 0) || !(cap > 0)) return notify.error("Capital, gain et perte potentiels requis (> 0).");
    setBusy(true);
    try {
      await api.post("/workspace/decisions", {
        title: f.title.trim(),
        question: f.thesis.trim() || `Faut-il détenir ${f.instrument.trim()} sur ${f.horizon} jours ?`,
        context: { system: "financial_markets", ref_type: "instrument",
                   ref_id: f.instrument.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-"), currency: "USD" },
        capital_at_risk_chf: cap,
        time_horizon_days: Number(f.horizon) || 14,
        scenarios: [
          { label: `Gain (horizon ${f.horizon}j)`, payoff_chf: up,
            probability: { value: p, source: f.source.trim(), method: "expert", confidence: Number(f.confidence) } },
          { label: `Perte (horizon ${f.horizon}j)`, payoff_chf: -down,
            probability: { value: Math.round((1 - p) * 1000) / 1000, source: f.source.trim(), method: "expert", confidence: Number(f.confidence) } },
        ],
      });
      notify.success("Décision manuelle journalisée — snapshot à trancher dans le Journal.");
      onDone();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  return (
    <div className="noam-card p-3 space-y-2" data-testid="manual-fm-form">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
        <CandlestickChart className="w-3.5 h-3.5 text-[#43C6D8]" /> Décision manuelle — Financial Markets
      </div>
      <div className="flex gap-1.5 flex-wrap">
        <input value={f.instrument} onChange={set("instrument")} placeholder="Instrument / actif *" className="noam-input text-[10px] w-44" data-testid="fm-manual-instrument" />
        <input value={f.title} onChange={set("title")} placeholder="Titre de la décision *" className="noam-input text-[10px] flex-1 min-w-[200px]" data-testid="fm-manual-title" />
        <input value={f.thesis} onChange={set("thesis")} placeholder="Thèse (pourquoi maintenant ?)" className="noam-input text-[10px] flex-1 min-w-[200px]" data-testid="fm-manual-thesis" />
      </div>
      <div className="flex gap-2 flex-wrap items-end">
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600">Capital USD *
          <input type="number" value={f.capital} onChange={set("capital")} placeholder="ex. 10000 (à saisir)" className="noam-input text-[10px] w-28" data-testid="fm-manual-capital" />
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600" title="Probabilité que le scénario de gain se réalise (0-1)">P(gain)
          <input type="number" step="0.05" min="0.01" max="0.99" value={f.pWin} onChange={set("pWin")} className="noam-input text-[10px] w-20" data-testid="fm-manual-pwin" />
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600">Gain pot. USD *
          <input type="number" value={f.upside} onChange={set("upside")} placeholder="ex. 1800 (à saisir)" className="noam-input text-[10px] w-28" data-testid="fm-manual-upside" />
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600">Perte pot. USD *
          <input type="number" value={f.downside} onChange={set("downside")} placeholder="ex. 900 (à saisir)" className="noam-input text-[10px] w-28" data-testid="fm-manual-downside" />
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600 flex-1 min-w-[180px]">Source de l'estimation *
          <input value={f.source} onChange={set("source")} placeholder="ex. analyse technique + on-chain" className="noam-input text-[10px] w-full" data-testid="fm-manual-source" />
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600" title="Qualité de VOTRE estimation (0-1) — distincte de P(gain) : on peut annoncer P=0.7 en étant peu sûr de son estimation">Confiance
          <input type="number" step="0.1" min="0" max="1" value={f.confidence} onChange={set("confidence")} className="noam-input text-[10px] w-20" />
        </label>
        <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-zinc-600">Horizon (j)
          <input type="number" min="1" value={f.horizon} onChange={set("horizon")} className="noam-input text-[10px] w-20" />
        </label>
        <button onClick={submit} disabled={busy} className="btn-primary text-[10px]" data-testid="fm-manual-create"><Plus className="w-3 h-3" /> Journaliser</button>
      </div>
      <MemoryRecallPanel system="financial_markets" p={isNaN(p) ? null : p} refKey={f.instrument.trim() || null} />
    </div>
  );
}
